import "dayjs/locale/en";
import "dayjs/locale/th";
import "dayjs/locale/zh-cn";

import * as companyService from "../../../services/company";
import * as originPackageService from "../../../services/originPackage";
import * as settings from "../../../services/settings";
import * as warehouseService from "../../../services/warehouse";

import { ArrowLeftOutlined, DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography,
  notification,
} from "antd";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { getRouteLabel, isEmpty } from "../../../utils/common";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePermission, useUser } from "../../../hooks/usePermission";

import AuthGroup from "../../../../configs/authgroup";
import { ModalDelete } from "../../../Components/model/modal/delete";
import dayjs from "dayjs";
import debounce from "lodash.debounce";
import { exportComponent } from "../../../Components/model/modal/export";
import permissions from "../../../../configs/permission";
import { useAccessToken } from "../../../contexts/AccessTokenContext";
import { useTranslation } from "react-i18next";

const { Link } = Typography;

const { RangePicker } = DatePicker;
const initPage = { limit: 10, page: 1, values: {} };
const EXPORT_ALL_MAX = 5000;

/** Coalesces concurrent identical requests (e.g. React Strict Mode double mount). */
const pendingOriginPackageByKey = new Map();
const pendingWarehousesByKey = new Map();
const pendingCompanyListInFlightByToken = new Map();

const initialValues = {
  search_date: [dayjs().startOf("day"), dayjs().endOf("day")],
  search: undefined,
  search_package_status: "all",
  search_route: "all",
  search_origin: "all",
  search_destination: "all",
  search_customer: "all",
  search_package_type: "all",
};
// Cache company list to avoid repeated API calls (keyed by accessToken)
const companyListCache = { token: null, data: null };
export const OriginPackagePage = () => {
  const { hasPermission, isSuperuser, hasGroup } = usePermission();
  const {
    companyId: userCompanyId,
    saleId: userSaleId,
    customerId: userCustomerId,
    customerCode: userCustomerCode,
  } = useUser();
  const isCompanySaleUser = !isSuperuser && hasGroup(AuthGroup.CompanySale);
  const { accessToken } = useAccessToken();
  const [fromSearch] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const { t, i18n } = useTranslation("packages", "buttons", "common");
  const { t: tOps } = useTranslation("options", { keyPrefix: "Package" });
  const [search, setSearch] = useState(initPage);
  const [originPackageData, setOriginPackageData] = useState([]);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [prepareDeleteData, setPrepareDeleteData] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowsValues, setSelectedRowsValues] = useState([]);
  const [countSelect, setCountSelect] = useState(0);
  const [isOpenExport, setIsOpenExport] = useState(false);
  const [isExportAll, setIsExportAll] = useState(false);
  const [selectedSearchCredit, setSelectedSearchCredit] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [destination, setDestination] = useState([]);
  const [origin, setOrigin] = useState([]);
  const [listCustomer, setListCustomer] = useState([]);
  const [tempSearch, setTempSearch] = useState();

  // const [packageStatus, setPackageStatus] = useState(0)
  const navigate = useNavigate();
  const { Title } = Typography;
  const [total, setTotal] = useState(0);

  const [pageOt, setPage] = useState(1);
  const [searchData, setSearchData] = useState("");
  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = useCallback(
    (type, message, description) => {
      api[type]({ message, description });
    },
    [api]
  );

  //Set localstorage on lang for sent to origin package edit (New tab)
  localStorage.setItem("lang", i18n.language);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, value) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRowsValues(value);
      setCountSelect(value.length);
      setIsOpenExport(selectedRowKeys.length > 0 ? false : true);
      setIsExportAll(false);
    },
  };

  const onDeleteOriginPackage = async (data) => {
    const prepareDeleteOriginPackage = {
      id: data.id,
    };
    setLoading(true);
    try {
      await originPackageService.deleteOriginPackage(accessToken, prepareDeleteOriginPackage);
      openNotificationWithIcon(
        settings.NotificationType.SUCCESS,
        <>{"Success."}</>,
        <>{"Delete origin package success."}</>
      );
      setPrepareDeleteData({});
      setIsOpenDelete(false);
      await fetchOriginPackage({ ...search });
    } catch (err) {
      console.error("[OriginPackagePage] onDeleteOriginPackage: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
    } finally {
      setLoading(false);
    }
  };

  const onDeleteMutiPackage = async (data) => {
    const payload = { ids: data };
    setLoading(true);
    try {
      await originPackageService.deleteMultiPackageService(accessToken, payload);
      openNotificationWithIcon(settings.NotificationType.SUCCESS, "Success.", "Delete origin package success.");
      clearSelection();
      setPrepareDeleteData({});
      setIsOpenDelete(false);
      await fetchOriginPackage({ ...search });
    } catch (err) {
      console.error("[OriginPackagePage] onDeleteMutiPackage: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, "ERROR.", `${message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchOriginPackage = useCallback(
    async (param) => {
      const requestKey = JSON.stringify(param);
      const existing = pendingOriginPackageByKey.get(requestKey);
      if (existing) {
        return existing;
      }

      const promise = (async () => {
        try {
          const data = await originPackageService.getOriginPackage(accessToken, param);
          setTotal(data.totalCount);
          setOriginPackageData(data.queryOriginPackage);
          setSearch((prev) => ({
            ...prev,
            ...param,
            values: param.values ?? prev.values ?? {},
          }));
        } catch (err) {
          console.error("[OriginPackagePage] fetchOriginPackage: ", err);
          const { message } = settings.getErrorMessage(err);
          openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
        } finally {
          pendingOriginPackageByKey.delete(requestKey);
        }
      })();

      pendingOriginPackageByKey.set(requestKey, promise);
      return promise;
    },
    [accessToken, openNotificationWithIcon]
  );

  const onSearch = async (value) => {
    setLoading(true);
    try {
      const {
        search,
        search_date,
        search_customer,
        search_destination,
        search_origin,
        search_route,
        search_package_status,
        search_note,
        search_package_type,
        ...data
      } = value;
      let start_date;
      let end_date;
      if (data && search_date) {
        start_date = dateRange[0] || undefined;
        end_date = dateRange[1] || undefined;
      }

      const searchText = search != undefined ? search.trim() : ""; // ✅ ป้องกัน undefined
      const values = {
        start_date: start_date,
        end_date: end_date,
        reference: searchText,
        tracking: searchText,
        package_status: search_package_status,
        route: search_route,
        origin: search_origin,
        destination: search_destination,
        customer: search_customer,
        package_description: searchText,
        container: searchText,
        note: search_note,
        package_type: search_package_type,
      };

      setTempSearch(value);

      await fetchOriginPackage({
        values,
        page: value.page ?? initPage.page,
        limit: value.limit ?? initPage.limit,
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async () => {
    const values = fromSearch.getFieldsValue();
    try {
      setLoading(true);
      await onSearch({ ...values, initPage });
    } finally {
      setLoading(false);
    }
  };

  const onReset = async () => {
    fromSearch.resetFields();
    const scopedCustomer = isCompanySaleUser && userCustomerId != null ? String(userCustomerId) : undefined;
    if (scopedCustomer) {
      fromSearch.setFieldsValue({ search_customer: scopedCustomer });
    }
    try {
      setCountSelect(0);
      setLoading(true);
      await fetchOriginPackage(scopedCustomer ? { ...initPage, values: { customer: scopedCustomer } } : initPage);
    } finally {
      setLoading(false);
    }
  };

  const clearSelection = () => {
    setSelectedRowKeys([]);
    setSelectedRowsValues([]);
    setCountSelect(0);
    setIsExportAll(false);
    setIsOpenExport(true);
  };
  const onSelectAll = async () => {
    if (total > EXPORT_ALL_MAX) {
      openNotificationWithIcon(
        settings.NotificationType.WARNING,
        t("Warning", { ns: "common" }),
        t("Export_All_Limit_Error", {
          ns: "error",
          max: EXPORT_ALL_MAX.toLocaleString(),
          count: total.toLocaleString(),
        })
      );
      setCountSelect(EXPORT_ALL_MAX);
      setIsExportAll(true);
      setIsOpenExport(total > 0 ? false : true);
      setSelectedRowKeys([]);
      setSelectedRowsValues([]);
      return;
    }
    setCountSelect(total);
    setIsExportAll(true);
    setIsOpenExport(total > 0 ? false : true);
    setSelectedRowKeys([]);
    setSelectedRowsValues([]);
  };

  const handleDateChange = (dates, dateStrings) => {
    setDateRange(dateStrings);
  };

  const onExport = async () => {
    if (isExportAll && total > EXPORT_ALL_MAX && countSelect > EXPORT_ALL_MAX) {
      openNotificationWithIcon(
        settings.NotificationType.WARNING,
        t("Warning", { ns: "common" }),
        t("Export_All_Limit_Error", {
          ns: "error",
          max: EXPORT_ALL_MAX.toLocaleString(),
          count: total.toLocaleString(),
        })
      );
      setCountSelect(EXPORT_ALL_MAX);
      return;
    }
    setLoading(true);
    // Define custom headers with corresponding keys
    const headerMap = [
      {
        label: t("Origin_Package_Export_Excel.Waybill_Number"),
        key: "waybill_number",
      },
      {
        label: t("Origin_Package_Export_Excel.Warehousing_Time"),
        key: "warehousing_time",
      },
      {
        label: t("Origin_Package_Export_Excel.Warehouse_Entry_Number"),
        key: "warehouse_entry_number",
      },
      { label: t("Origin_Package_Export_Excel.Reference"), key: "reference" },
      { label: t("Origin_Package_Export_Excel.Custom_Number"), key: "custom_no" },
      {
        label: t("Origin_Package_Export_Excel.Combined_Box"),
        key: "combined_box",
      },
      {
        label: t("Origin_Package_Export_Excel.Tracking"),
        key: "tracking",
      },
      { label: t("Origin_Package_Export_Excel.K_M"), key: "K_M" },
      { label: t("Origin_Package_Export_Excel.User_Name"), key: "user_name" },
      { label: t("Origin_Package_Export_Excel.Code"), key: "code" },
      { label: t("Origin_Package_Export_Excel.Full_Name"), key: "full_name" },
      { label: t("Origin_Package_Export_Excel.Goods_Name"), key: "goods_name" },
      { label: t("Origin_Package_Export_Excel.Address"), key: "address" },
      { label: t("Origin_Package_Export_Excel.Box"), key: "box" },
      {
        label: t("Origin_Package_Export_Excel.Total_Amount"),
        key: "total_amount",
      },
      { label: t("Origin_Package_Export_Excel.Weight_KG"), key: "weight_kg" },
      {
        label: t("Origin_Package_Export_Excel.Total_Weight_KG"),
        key: "total_weight_kg",
      },
      {
        label: t("Origin_Package_Export_Excel.Weight_Type"),
        key: "weight_type",
      },
      { label: t("Origin_Package_Export_Excel.Length_CM"), key: "length_CM" },
      { label: t("Origin_Package_Export_Excel.Width_CM"), key: "width_CM" },
      { label: t("Origin_Package_Export_Excel.Height_CM"), key: "height_CM" },
      {
        label: t("Origin_Package_Export_Excel.OD"),
        key: "od",
      },
      {
        label: t("Origin_Package_Export_Excel.WC"),
        key: "wc",
      },
      {
        label: t("Origin_Package_Export_Excel.IN"),
        key: "in",
      },
      { label: t("Origin_Package_Export_Excel.Volume"), key: "volume" },
      {
        label: t("Origin_Package_Export_Excel.Total_Volume"),
        key: "total_volume",
      },
      {
        label: t("Origin_Package_Export_Excel.Type_Of_Goods"),
        key: "type_of_goods",
      },
      {
        label: t("Origin_Package_Export_Excel.Additional_Service"),
        key: "additional_Service",
      },
      {
        label: t("Origin_Package_Export_Excel.Density_KG_CBM"),
        key: "density_kg_cbm",
      },
    ];
    const fileName = `origin_warehouse-${dayjs().format("YYYYMMDD")}${dayjs().format("HHmmss")}`;
    let formattedData;
    let sumTotalReceived = 0.0;
    let sumTotalWeight = 0.0;
    let sumTotalVolume = 0.0;
    setIsOpenExport(true);
    const value = search?.values || {};
    let start_date;
    let end_date;
    if (value && (value?.search_date || value?.start_date || value?.end_date)) {
      start_date = dateRange[0] || undefined;
      end_date = dateRange[1] || undefined;
    }
    const params = {
      start_date: start_date,
      end_date: end_date,
      reference: value.reference,
      tracking: value.tracking,
      package_status:
        value.package_status !== "all" && value.package_status !== undefined ? value.package_status : undefined,
      route: value.route !== "all" && value.route !== undefined ? value.route : undefined,
      origin: value.origin !== "all" && value.origin !== undefined ? value.origin : undefined,
      destination: value.destination !== "all" && value.destination !== undefined ? value.destination : undefined,
      customer: value.customer,
      package_description: value.package_description,
      page: 1,
      limit: isExportAll ? Math.min(Number(total), countSelect) : total,
    };
    if (value.search_customer !== "all" && value.search_customer !== undefined) {
      const splitArray = value.search_customer.split(",");
      params.customer = splitArray[2];
    } else {
      params.customer = "all";
      fromSearch.setFieldsValue({ search_customer: "all" }); // ✅ รีเซตในฟอร์ม
    }
    if (isExportAll) {
      try {
        const response = await originPackageService.exportOriginPackage(accessToken, params);
        if (!response?.data) {
          throw new Error("Export failed: empty response from server.");
        }
        // สร้างลิงก์สำหรับดาวน์โหลดไฟล์
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName + ".xlsx"); // ชื่อไฟล์
        document.body.appendChild(link);
        link.click();
        // ทำความสะอาดลิงก์
        link.parentNode.removeChild(link);
        clearSelection();
        setLoading(false);
      } catch (err) {
        console.error("[OriginPackagePage] onExport all: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
        setIsOpenExport(false);
        setLoading(false);
      }
    } else {
      try {
        formattedData = selectedRowsValues.map((item) => {
          const fullName =
            item.app_company?.company_code && item.app_sale?.sale_code && item.app_customer?.customer_code
              ? `${item.app_company.company_code}-${item.app_sale.sale_code}${item.app_customer.customer_code}`
              : "-";
          const address = item.app_warehouse_app_originpackage_origin_idToapp_warehouse
            ? item.app_warehouse_app_originpackage_origin_idToapp_warehouse?.warehouse_code
            : "-";
          const additionalService = item.app_originpackageservice
            ? item.app_originpackageservice
                .map((element) => {
                  return element?.package_service ?? "-";
                })
                .join(", ")
            : "-";

          const originPackageServiceOD =
            item.app_originpackageservice.find((item) => item.package_service === "OD")?.service_cost || 0;
          const originPackageServiceWC =
            item.app_originpackageservice.find((item) => item.package_service === "WC")?.service_cost || 0;
          const originPackageServiceIN =
            item.app_originpackageservice.find((item) => item.package_service === "IN")?.service_cost || 0;

          sumTotalReceived += Number(item.total_received);
          sumTotalWeight += Number(item.total_weight);
          sumTotalVolume += Number(item.total_volume);
          return {
            waybill_number: item.tracking || "-",
            warehousing_time: dayjs(item.created_at).format("YYYY.MM.DD") || "-",
            warehouse_entry_number: item.shipping || "-",
            reference: item.reference || "-",
            custom_no: item.custom_no || "",
            tracking: item.tracking || "-",
            combined_box: item.box || "-",
            K_M: item.route || "-",
            user_name: item.app_company.company_code || "-",
            code: item.app_customer.customer_code || "-",
            full_name: fullName,
            goods_name: item.package_description || "-",
            address: address,
            box: item.total_received || "-",
            total_amount: item.total_amount || "-",
            weight_kg: item.weight || "-",
            total_weight_kg: item.total_weight || "-",
            weight_type: item.weight_type || "-",
            length_CM: item.length || "-",
            width_CM: item.width || "-",
            height_CM: item.height || "-",
            od: originPackageServiceOD || 0,
            wc: originPackageServiceWC || 0,
            in: originPackageServiceIN || 0,
            volume: Number(item.volume).toFixed(2) || "-",
            total_volume: Number(item.total_volume).toFixed(2) || "-",
            type_of_goods: item.package_type || "-",
            additional_Service: additionalService || "-",
            density_kg_cbm: parseInt(Math.round(item.density)) || "-",
          };
        });
        // Add footer row (count data)
        const footerRow = {
          waybill_number: "",
          warehousing_time: "",
          warehouse_entry_number: "",
          reference: "",
          custom_no: "",
          combined_box: "",
          tracking: "",
          K_M: "",
          user_name: "",
          code: "",
          full_name: "",
          goods_name: "",
          address: "",
          box: sumTotalReceived || "",
          total_amount: "",
          weight_kg: "",
          total_weight_kg: sumTotalWeight || "",
          weight_type: "",
          length_CM: "",
          width_CM: "",
          height_CM: "",
          od: "",
          wc: "",
          in: "",
          volume: "",
          total_volume: sumTotalVolume || "",
          type_of_goods: "",
          additional_Service: "",
          density_kg_cbm: "",
        };
        formattedData.push(footerRow);
        exportComponent({ formattedData, fileName, headerMap });
        clearSelection();
        setLoading(false);
      } catch (err) {
        console.error("[OriginPackagePage] onExport: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
        setIsOpenExport(false);
        setLoading(false);
      }
    }
  };

  const fetchWareHouse = useCallback(
    async (param) => {
      const requestKey = JSON.stringify(param);
      const existing = pendingWarehousesByKey.get(requestKey);
      if (existing) {
        return existing;
      }

      const promise = (async () => {
        let resDestination = [{ label: tOps("All"), value: "all" }];
        let resOrigin = [{ label: tOps("All"), value: "all" }];
        try {
          const data = await warehouseService.getWarehouses(accessToken, param);
          if (data !== undefined) {
            data.warehouse.map((value) => {
              if (value.is_destination === true) {
                resDestination.push({
                  value: value.id,
                  label: value.warehouse_code,
                });
              }
              if (value.is_origin === true) {
                resOrigin.push({
                  value: value.id,
                  label: value.warehouse_code,
                });
              }
            });
          }
          setDestination(resDestination);
          setOrigin(resOrigin);
        } catch (err) {
          console.error("[OriginPackagePage] fetchWareHouse: ", err);
          const { message } = settings.getErrorMessage(err);
          openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
        } finally {
          pendingWarehousesByKey.delete(requestKey);
        }
      })();

      pendingWarehousesByKey.set(requestKey, promise);
      return promise;
    },
    [accessToken, tOps, openNotificationWithIcon]
  );

  const fetchCustomerList = useCallback(async () => {
    try {
      let data;
      if (companyListCache.token === accessToken && companyListCache.data) {
        data = companyListCache.data;
      } else {
        const inFlightKey = accessToken;
        let fetchPromise = pendingCompanyListInFlightByToken.get(inFlightKey);
        if (!fetchPromise) {
          fetchPromise = (async () => {
            try {
              return await companyService.getCompanysList(accessToken);
            } finally {
              pendingCompanyListInFlightByToken.delete(inFlightKey);
            }
          })();
          pendingCompanyListInFlightByToken.set(inFlightKey, fetchPromise);
        }
        data = await fetchPromise;
        companyListCache.token = accessToken;
        companyListCache.data = data;
      }

      const resCustomer = data.flatMap((value) =>
        (value.app_sale ?? []).flatMap((saleValue) =>
          (saleValue.app_customer ?? []).map((customerValue) => {
            const codePart = `${value.company_code}-${saleValue.sale_code}${customerValue.customer_code}`;
            return {
              value: `${customerValue.id}`,
              label: codePart,
            };
          })
        )
      );
      if (isCompanySaleUser && userCustomerId != null) {
        const defaultCustomerValue = String(userCustomerId);
        const matchedCustomer = resCustomer.find((item) => item.value === defaultCustomerValue);
        const matchedCompany = data.find((item) => String(item.id) === String(userCompanyId));
        const matchedSale = (matchedCompany?.app_sale ?? []).find((item) => String(item.id) === String(userSaleId));
        const fallbackCodePart =
          matchedCompany?.company_code && matchedSale?.sale_code && userCustomerCode
            ? `${matchedCompany.company_code}-${matchedSale.sale_code}${userCustomerCode}`
            : undefined;
        const fallbackLabel =
          fallbackCodePart && fallbackCodePart.trim().length > 0
            ? fallbackCodePart
            : userCustomerCode && String(userCustomerCode).trim().length > 0
              ? String(userCustomerCode)
              : `Customer ${defaultCustomerValue}`;
        const scopedOption = matchedCustomer ?? {
          value: defaultCustomerValue,
          label: fallbackLabel,
        };

        // CompanySale: ต้องเห็นเฉพาะ customer ของตัวเองจาก JWT เสมอ
        setListCustomer([scopedOption]);
        fromSearch.setFieldsValue({ search_customer: defaultCustomerValue });
        await fetchOriginPackage({
          ...initPage,
          values: { customer: defaultCustomerValue },
        });
      } else {
        const allOption = { value: "all", label: tOps("All") };
        setListCustomer([allOption, ...resCustomer]);
      }
      setLoadingCustomer(false);
    } catch (err) {
      console.error("[OriginPackagePage] fetchCustomerList: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      setLoadingCustomer(false);
    }
  }, [
    accessToken,
    tOps,
    i18n.language,
    openNotificationWithIcon,
    fromSearch,
    isCompanySaleUser,
    userCompanyId,
    userSaleId,
    userCustomerId,
    userCustomerCode,
    fetchOriginPackage,
  ]);

  const hasInitialized = useRef(false);

  const disableFutureDates = useCallback((current) => {
    return current && current.isAfter(dayjs().endOf("day"));
  }, []);

  useEffect(() => {
    if (!accessToken || hasInitialized.current) return;
    hasInitialized.current = true;

    let cancelled = false;
    setLoading(true);
    setIsOpenExport(true);
    setCountSelect(0);
    setSelectedRowKeys([]);
    setSelectedRowsValues([]);
    setLoadingCustomer(true);

    (async () => {
      const initialParam = { ...initPage };
      try {
        // โหลดตารางและ warehouse ก่อน ไม่รอ customer list เพื่อให้หน้าโหลดไวขึ้น
        await Promise.allSettled([
          fetchOriginPackage(initialParam),
          fetchWareHouse({ ...initialParam, is_active: "true" }),
        ]);
        // โหลด customer list แยก (มี cache ถ้าเคยโหลดแล้วจะไม่เรียก API ซ้ำ)
        fetchCustomerList();
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      // รีเซ็ตเพื่อให้ Strict Mode รัน effect ครั้งที่สองได้ ไม่ให้ loading ค้าง
      hasInitialized.current = false;
    };
  }, [accessToken, fetchOriginPackage, fetchWareHouse, fetchCustomerList]);

  // Filter search from select
  const handleSearchCustomer = debounce(async (value) => {
    if (!value) {
      setSearchData("");
    } else if (value !== searchData) {
      setSearchData("");
    }
    setSearchData(value);
    await filterCustomer(value);
  }, 300);

  // Filter `option.label` match the user type `input`
  const filterOption = (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const filterCustomer = async (saleData) => {
    await listCustomer.filter((value) => value.label.includes(saleData));
  };

  const columns = [
    {
      title: t("Package_Table.Table_Delete"),
      align: "center",
      key: "delete",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          disabled={!hasPermission(permissions.OriginPackage.DELETE)}
          onClick={() => {
            setIsOpenDelete(true);
            setPrepareDeleteData(record);
          }}
        >
          <DeleteOutlined />
        </Button>
      ),
    },
    {
      title: t("Package_Table.Table_Reference"),
      width: 150,
      dataIndex: "reference",
      render: (_, row) => (
        <RouterLink to={`/package/originpackage/create?id=${row.id}&mode=edit`} target="_blank">
          {row.reference}
        </RouterLink>
      ),
    },
    {
      title: t("Package_Table.Table_Customer"),
      dataIndex: "app_customer",
      width: 200,
      render: (_, record) =>
        record.app_customer
          ? record.app_company?.company_code + "-" + record.app_sale?.sale_code + record.app_customer.customer_code
          : "-",
    },
    {
      title: t("Package_Table.Table_Custom_Number"),
      width: 150,
      dataIndex: "custom_no",
    },
    {
      title: t("Package_Table.Table_Box"),
      dataIndex: "box",
      width: 200,
      render: (_, record) => record.box || "-",
    },
    {
      title: t("Package_Table.Table_Tracking"),
      dataIndex: "tracking",
      width: 150,
      render: (_, record) => record.tracking || "-",
    },
    {
      title: t("Package_Table.Table_Route"),
      width: 150,
      dataIndex: "route",
      render: (_, record) => {
        const label = getRouteLabel(record?.route, i18n);
        return <p>{label}</p>;
      },
    },
    {
      title: t("Package_Table.Table_Origin"),
      width: 150,
      dataIndex: "app_warehouse_app_originpackage_origin_idToapp_warehouse",
      render: (_, record) => {
        const origin = record.app_warehouse_app_originpackage_origin_idToapp_warehouse
          ? record.app_warehouse_app_originpackage_origin_idToapp_warehouse.warehouse_code
          : "-";
        return <p>{origin}</p>;
      },
    },
    {
      title: t("Package_Table.Table_Origin_Transportation"),
      width: 150,
      dataIndex: "origin_transportation",
      render: (_, record) =>
        (record.app_originpackagetransportation || []).map((values, index) => {
          if (index == 0) {
            return values.app_origintransportation.container;
          }
        }),
    },
    {
      title: t("Package_Table.Table_Destination"),
      dataIndex: "app_warehouse_app_originpackage_destination_idToapp_warehouse",
      width: 150,
      render: (_, record) => {
        const destination = record.app_warehouse_app_originpackage_destination_idToapp_warehouse
          ? record.app_warehouse_app_originpackage_destination_idToapp_warehouse.warehouse_code
          : "-";
        return <p>{destination}</p>;
      },
    },
    {
      title: t("Package_Table.Table_Package_Service"),
      dataIndex: "app_originpackageservice",
      width: 150,
      render: (_, record) => {
        const services = record.app_originpackageservice || [];
        if (services.length === 0) return "-";

        const order = ["OD", "WC", "IN"];

        const filtered = services
          .filter((el) => Number(el.service_cost) > 0)
          .sort((a, b) => order.indexOf(a.package_service) - order.indexOf(b.package_service))
          .map((el) => el.package_service);

        return filtered.length > 0 ? filtered.join(", ") : "-";
      },
    },
    {
      title: t("Package_Table.Table_Total_Received"),
      dataIndex: "total_received",
      width: 150,
      render: (_, record) => record.total_received || "-",
    },
    {
      title: t("Package_Table.Table_Total_Sent"),
      dataIndex: "total_sent",
      width: 150,
      render: (_, record) => {
        const status = packageStatus.find((item) => item.value === record.package_status);
        const totalSent = status.value === "0" ? "-" : record?.app_originpackagetransportation[0]?.total_received;
        return totalSent || "-";
      },
    },
    {
      title: t("Package_Table.Table_Description"),
      dataIndex: "package_description",
      width: 150,
      render: (_, record) => record.package_description || "",
    },
    {
      title: t("Package_Table.Table_Package_Status"),
      dataIndex: "package_status",
      width: 200,
      render: (_, record) => {
        const status = packageStatus.find((item) => item.value === record.package_status);
        return status ? status.label : "";
      },
    },
    {
      title: t("Package_Table.Table_Type"),
      dataIndex: "package_type",
      width: 150,
      render: (_, record) => record.package_type || "",
    },
    {
      title: t("Package_Table.Table_Count"),
      dataIndex: "total_count",
      width: 150,
      render: (_, record) => record.total_count || 0,
    },
    {
      title: t("Package_Table.Table_Length"),
      dataIndex: "length",
      width: 150,
      render: (_, record) => record.length || 0,
    },
    {
      title: t("Package_Table.Table_Width"),
      dataIndex: "width",
      width: 150,
      render: (_, record) => record.width || 0,
    },
    {
      title: t("Package_Table.Table_Height"),
      dataIndex: "height",
      width: 150,
      render: (_, record) => record.height || 0,
    },
    {
      title: t("Package_Table.Table_Volume"),
      dataIndex: "volume",
      width: 150,
      render: (_, record) => record.volume || 0,
    },
    {
      title: t("Package_Table.Table_Total_Volume"),
      dataIndex: "total_volume",
      width: 150,
      render: (_, record) => record.total_volume || 0,
    },
    {
      title: t("Package_Table.Table_Weight"),
      dataIndex: "weight",
      width: 150,
      render: (_, record) => record.weight || 0,
    },
    {
      title: t("Package_Table.Table_Total_Weight"),
      dataIndex: "total_weight",
      width: 150,
      render: (_, record) => record.total_weight || 0,
    },
    {
      title: t("Package_Table.Table_OD"),
      dataIndex: "od",
      width: 150,
      render: (_, record) => {
        const originPackageServiceOD =
          record.app_originpackageservice.find((item) => item.package_service === "OD")?.service_cost || 0;
        return <p>{originPackageServiceOD}</p>;
      },
    },
    {
      title: t("Package_Table.Table_WC"),
      dataIndex: "wc",
      width: 150,
      render: (_, record) => {
        const originPackageServiceWC =
          record.app_originpackageservice.find((item) => item.package_service === "WC")?.service_cost || 0;
        return <p>{originPackageServiceWC}</p>;
      },
    },
    {
      title: t("Package_Table.Table_IN"),
      dataIndex: "in",
      width: 150,
      render: (_, record) => {
        const originPackageServiceIN =
          record.app_originpackageservice.find((item) => item.package_service === "IN")?.service_cost || 0;
        return <p>{originPackageServiceIN}</p>;
      },
    },
    {
      title: t("Package_Table.Table_Weight_Type"),
      dataIndex: "weight_type",
      width: 150,
      render: (_, record) => record.weight_type || "-",
    },
    {
      title: t("Package_Table.Table_Amount"),
      dataIndex: "amount",
      width: 150,
      render: (_, record) => record.amount || "0",
    },
    {
      title: t("Package_Table.Table_Total_Amount"),
      dataIndex: "total_amount",
      width: 150,
      render: (_, record) => record.total_amount || "0",
    },
    {
      title: t("Package_Table.Table_Density"),
      dataIndex: "density",
      width: 150,
      render: (_, record) => {
        const density = record.density || "0";
        return <p>{Math.round(density)}</p>;
      },
    },
    {
      title: t("Package_Table.Table_Origin_Package_Time"),
      width: 150,
      dataIndex: "origin_package_time",
      render: (_, record) =>
        record.origin_package_time != null ? dayjs(record.origin_package_time).format("YYYY-MM-DD hh:mm:ss A") : "-",
    },
    {
      title: t("Package_Table.Table_Origin_Transportation_Time"),
      width: 150,
      dataIndex: "origin_transportation_time",
      render: (_, record) =>
        record.origin_transportation_time != null
          ? dayjs(record.origin_transportation_time).format("YYYY-MM-DD hh:mm:ss A")
          : "-",
    },
    {
      title: t("Package_Table.Table_Destination_Package_Time"),
      width: 150,
      dataIndex: "destination_package_time",
      render: (_, record) =>
        record.destination_package_time != null
          ? dayjs(record.destination_package_time).format("YYYY-MM-DD hh:mm:ss A")
          : "-",
    },
    {
      title: t("Package_Table.Table_Destination_Delivery_Time"),
      width: 150,
      dataIndex: "destination_delivery_time",
      render: (_, record) =>
        record.destination_delivery_time != null
          ? dayjs(record.destination_delivery_time).format("YYYY-MM-DD hh:mm:ss A")
          : "-",
    },
    {
      title: t("Package_Table.Table_Created_At"),
      width: 150,
      dataIndex: "created_at",
      render: (_, record) =>
        record.created_at != null ? dayjs(record.created_at).format("YYYY-MM-DD hh:mm:ss A") : "-",
    },
    {
      title: t("Package_Table.Table_Updated_At"),
      width: 150,
      dataIndex: "updated_at",
      render: (_, record) =>
        record.updated_at != null ? dayjs(record.updated_at).format("YYYY-MM-DD hh:mm:ss A") : "-",
    },
    // Make a note of this and close it for now, as the user already knows about it but may need it later.
    // {
    //   title: t("Package_Table.Table_Note"),
    //   width: 150,
    //   dataIndex: "note",
    //   render: (_, record) => record.note || "-",
    // },
  ];

  const items = [
    { title: t("Page_Body_Tab_Menu.Home", { ns: "menus" }) },
    { title: t("Page_Body_Tab_Menu.Menu", { ns: "menus" }) },
    { title: t("Menu_Package.Title_Package", { ns: "menus" }) },
    {
      title: (
        <a href="/package/originpackage" style={{ fontWeight: 600, color: "black" }}>
          {t("Title_Origin_Package")}
        </a>
      ),
    },
  ];

  const packageStatus = [
    { value: "all", label: tOps("All") },
    { value: "0", label: tOps("Package_Status.At_The_Origin_Warehouse") },
    { value: "1", label: tOps("Package_Status.Transporting_To_The_Destination_Warehouse") },
    { value: "2", label: tOps("Package_Status.At_The_Destination_Warehouse") },
    { value: "3", label: tOps("Package_Status.Delivered") },
  ];

  const route = [
    { value: "all", label: tOps("All") },
    { value: "land", label: tOps("Route.Land") },
    { value: "sea", label: tOps("Route.Sea") },
  ];

  const packageType = [
    { value: "all", label: tOps("All") },
    { value: "A", label: "A" },
    { value: "B", label: "B" },
    { value: "C", label: "C" },
    { value: "D", label: "D" },
    { value: "E", label: "E" },
  ];

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row>
          <Col span={24}>
            <Breadcrumb items={items}></Breadcrumb>
            <Title
              level={4}
              style={{
                textWrap: "nowrap",
                display: "flex",
                gap: "7px",
                padding: "15px 0px",
              }}
            >
              <ArrowLeftOutlined style={{ fontSize: "20px" }} />
              {t("Title_Origin_Package")}
            </Title>
          </Col>
        </Row>
        <Row style={{ overflowX: "auto" }}>
          <Col span={12}>
            <Form
              form={fromSearch}
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              labelAlign="left"
              layout="horizontal"
              onFinish={onFinish}
              initialValues={initialValues}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  fromSearch.submit();
                }
              }}
            >
              <Row flex={1}>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item name="search_date" label={t("Label.Create_At")}>
                    <RangePicker
                      allowEmpty
                      style={{ width: "100%", minWidth: 200 }}
                      onChange={handleDateChange}
                      disabledDate={disableFutureDates}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item name="search" label={t("Label.Search")}>
                    <Input
                      size="default"
                      placeholder="Reference / Tracking / Description / Container "
                      style={{ minWidth: 200 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Label.Package_Status")} name="search_package_status" key="search_package_status">
                    <Select
                      placeholder={"Please Select Package Status"}
                      options={packageStatus}
                      style={{ minWidth: 200 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Label.Route")} name="search_route" key="search_route">
                    <Select placeholder={"Please Select Route"} options={route} style={{ minWidth: 200 }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Label.Origin")} name="search_origin" key="search_origin">
                    <Select placeholder={"Please Select Origin"} options={origin} style={{ minWidth: 200 }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Label.Destination")} name="search_destination" key="search_destination">
                    <Select placeholder={"Please Select Destination"} options={destination} style={{ minWidth: 200 }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Label.Customer")} name="search_customer" key="search_customer">
                    <Select
                      showSearch
                      style={{ width: "100%", minWidth: 200 }}
                      loading={loadingCustomer}
                      placeholder={"Please Select Customer"}
                      options={listCustomer}
                      onSearch={handleSearchCustomer}
                      filterOption={filterOption}
                      notFoundContent={loadingCustomer ? <Spin size="small" /> : "No data"}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item name="search_note" label={t("Label.Note")}>
                    <Input size="default" placeholder="Note " style={{ minWidth: 200 }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Label.Package_Type")} name="search_package_type" key="search_package_type">
                    <Select
                      placeholder={"Please Select Package Type"}
                      options={packageType}
                      style={{ minWidth: 200 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="end" align="middle">
                <Col span={24} style={{ textAlign: "end" }}>
                  <Form.Item>
                    <Button type="primary" htmlType="submit">
                      {t("BTN_Search", { ns: "buttons" })}
                    </Button>
                    <Button
                      type="default"
                      onClick={() => {
                        onReset();
                      }}
                      style={{ marginLeft: "5px" }}
                    >
                      {t("BTN_Reset", { ns: "buttons" })}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <div className="flex justify-between mb-4">
              {hasPermission(permissions.OriginPackage.DELETE) ? (
                <Button
                  type="primary"
                  disabled={isEmpty(selectedRowKeys)}
                  onClick={() => {
                    setIsOpenDelete(true);
                    setPrepareDeleteData(selectedRowKeys);
                  }}
                >
                  {t("Button_Packages.BTN_Delte_All")}
                </Button>
              ) : (
                <div />
              )}
              <Space direction="inline">
                {hasPermission(permissions.OriginPackage.EXPORT) && (
                  <Button type="primary" disabled={isOpenExport} onClick={onExport}>
                    {t("BTN_Export_Excel", { ns: "buttons" })}
                  </Button>
                )}
                {hasPermission(permissions.OriginPackage.ADD) && (
                  <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    onClick={() => {
                      navigate("/package/originpackage/create", {
                        state: { type: "CREATE" },
                      });
                    }}
                  >
                    {t("Button_Packages.BTN_Add")}
                  </Button>
                )}
              </Space>
            </div>
          </Col>
        </Row>
        <Row align="middle" justify="center">
          <Col span={24}>
            <Card>
              <Row align="middle" justify="center">
                <Col span={24}>
                  {`${t("Package_Table.Header_Select")}: ${countSelect} to ${total}`}&nbsp;
                  <Link onClick={onSelectAll} target="_blank">
                    {t("Package_Table.Header_Select_All")}
                  </Link>
                </Col>
              </Row>
              <Table
                loading={loading}
                rowSelection={rowSelection}
                columns={columns}
                dataSource={originPackageData}
                scroll={{ x: "max-content" }}
                rowKey={(record) => record.id}
                pagination={{
                  defaultPageSize: search.limit,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  current: search.page,
                  pageSize: search.limit,
                  total: total,
                  onChange: (page, limit) => {
                    onSearch({ ...tempSearch, page, limit });
                  },
                  showSizeChanger: true,
                }}
                style={{ cursor: "pointer" }}
              />
            </Card>
          </Col>
        </Row>
        <ModalDelete
          isOpen={isOpenDelete}
          handleCloseModal={() => {
            setIsOpenDelete(false);
          }}
          onClick={() => {
            if (Array.isArray(prepareDeleteData)) {
              onDeleteMutiPackage(prepareDeleteData);
            } else {
              onDeleteOriginPackage(prepareDeleteData);
            }
          }}
          dataDetail={prepareDeleteData}
          t={t}
        />
      </Spin>
    </>
  );
};
